import { useEffect, useState, useCallback } from "react";
import AdminLayout from "../components/AdminLayout";
import LoadingButton from "../components/LoadingButton";
import { PiggyBank, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, formatRupiah, formatDate } from "../lib/api";

const TYPES = [
  { key: "pokok", label: "Pokok" },
  { key: "wajib", label: "Wajib" },
  { key: "sukarela", label: "Sukarela" },
];

export default function AdminSimpanan() {
  const [rows, setRows] = useState([]);
  const [members, setMembers] = useState([]);
  const [filterType, setFilterType] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ member_id: "", type: "wajib", amount: "", note: "", transaction_date: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, m] = await Promise.all([
        api.get("/admin/simpanan", { params: filterType ? { type: filterType } : {} }),
        api.get("/admin/members", { params: { status: "active" } }),
      ]);
      setRows(s.data);
      setMembers(m.data);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  }, [filterType]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount) };
      if (!payload.transaction_date) delete payload.transaction_date;
      await api.post("/admin/simpanan", payload);
      toast.success("Simpanan berhasil dicatat");
      setOpen(false);
      setForm({ member_id: "", type: "wajib", amount: "", note: "", transaction_date: "" });
      load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight text-brand-ink">Simpanan</h1>
            <p className="text-brand-muted text-sm mt-1">Input simpanan pokok, wajib, dan sukarela anggota.</p>
          </div>
          <button onClick={() => setOpen(true)} data-testid="open-simpanan-form-btn" className="rounded-full bg-brand text-white px-5 py-2.5 text-sm flex items-center gap-2 hover:bg-brand-hover transition">
            <Plus size={14} /> Input Simpanan
          </button>
        </div>

        <div className="flex items-center gap-1 flex-wrap">
          <FilterChip active={filterType === ""} onClick={() => setFilterType("")} testid="simpanan-filter-all">Semua Tipe</FilterChip>
          {TYPES.map((t) => (
            <FilterChip key={t.key} active={filterType === t.key} onClick={() => setFilterType(t.key)} testid={`simpanan-filter-${t.key}`}>
              {t.label}
            </FilterChip>
          ))}
        </div>

        {error && <div className="rounded-2xl bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3">{error}</div>}

        <div className="bg-white rounded-3xl border border-brand/10 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="simpanan-table">
              <thead className="bg-brand-soft/60 text-left text-brand-muted">
                <tr>
                  <th className="px-4 py-3">Tanggal</th>
                  <th className="px-4 py-3">Anggota</th>
                  <th className="px-4 py-3">Tipe</th>
                  <th className="px-4 py-3 text-right">Jumlah</th>
                  <th className="px-4 py-3">Catatan</th>
                </tr>
              </thead>
              <tbody>
                {loading ? <tr><td colSpan={5} className="px-4 py-10 text-center text-brand-muted">Memuat…</td></tr>
                : rows.length === 0 ? <tr><td colSpan={5} className="px-4 py-10 text-center text-brand-muted">Belum ada transaksi simpanan</td></tr>
                : rows.map((r) => (
                  <tr key={r.id} className="border-t border-brand/10 hover:bg-brand-soft/30">
                    <td className="px-4 py-3">{formatDate(r.transaction_date)}</td>
                    <td className="px-4 py-3"><div className="font-medium text-brand-ink">{r.member_name}</div><div className="text-xs text-brand-muted">{r.member_number}</div></td>
                    <td className="px-4 py-3"><span className="inline-flex items-center gap-1 rounded-full bg-brand-soft text-brand text-xs px-2.5 py-1"><PiggyBank size={12} /> {r.type}</span></td>
                    <td className="px-4 py-3 text-right font-semibold text-brand-ink">{formatRupiah(r.amount)}</td>
                    <td className="px-4 py-3 text-brand-muted">{r.note || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-brand-ink/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-3" onClick={() => setOpen(false)}>
          <form onSubmit={submit} className="bg-white rounded-3xl w-full sm:max-w-md p-5 sm:p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="simpanan-form">
            <div className="font-heading font-bold text-lg text-brand-ink">Input Simpanan</div>

            <FormField label="Anggota">
              <select required value={form.member_id} onChange={(e) => setForm((p) => ({ ...p, member_id: e.target.value }))} className="inp" data-testid="simpanan-member-select">
                <option value="">— Pilih anggota —</option>
                {members.map((m) => (<option key={m.id} value={m.id}>{m.member_number ? `[${m.member_number}] ` : ""}{m.full_name}</option>))}
              </select>
            </FormField>
            <FormField label="Tipe Simpanan">
              <select value={form.type} onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))} className="inp" data-testid="simpanan-type-select">
                {TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </FormField>
            <FormField label="Jumlah (Rp)">
              <input required type="number" min="0" value={form.amount} onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))} className="inp" data-testid="simpanan-amount-input" />
            </FormField>
            <FormField label="Tanggal">
              <input type="date" value={form.transaction_date} onChange={(e) => setForm((p) => ({ ...p, transaction_date: e.target.value }))} className="inp" data-testid="simpanan-date-input" />
            </FormField>
            <FormField label="Catatan">
              <input value={form.note} onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))} className="inp" data-testid="simpanan-note-input" />
            </FormField>

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setOpen(false)} disabled={submitting} className="rounded-full border border-brand/20 px-4 py-2 text-sm disabled:opacity-60">Batal</button>
              <LoadingButton type="submit" loading={submitting} loadingText="Menyimpan…" data-testid="simpanan-submit-btn" className="rounded-full bg-brand text-white px-5 py-2 text-sm hover:bg-brand-hover">Simpan</LoadingButton>
            </div>
            <style>{`.inp{width:100%;border-radius:12px;border:1px solid rgba(11,110,110,0.2);padding:9px 12px;font-size:14px;background:white;outline:none}.inp:focus{box-shadow:0 0 0 3px rgba(11,110,110,0.15);border-color:rgba(11,110,110,0.4)}`}</style>
          </form>
        </div>
      )}
    </AdminLayout>
  );
}

function FilterChip({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={"rounded-full px-3 py-1.5 text-xs " + (active ? "bg-brand text-white" : "bg-white border border-brand/15 text-brand hover:bg-brand-soft")}>
      {children}
    </button>
  );
}

function FormField({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold text-brand-ink mb-1.5 uppercase tracking-[0.16em]">{label}</div>
      {children}
    </label>
  );
}
